import { useMutation, useQuery } from '@apollo/client'
import {
  BodyText,
  Button,
  CaptionText,
  Col,
  HeadingText,
  Link,
  Row,
} from '@paypalcorp/pp-react'
import { navigate } from '@reach/router'
import { merge } from 'lodash-es'
import ReadOnlyCard from 'ppbl/components/ReadOnlyCard'
import SpinnerAdapter from 'ppbl/components/SpinnerAdapter'
import { updateApplicationCachedData } from 'ppbl/graphql/__utils__'
import {
  createApplicationMutation,
  updateApplicationMutation,
} from 'ppbl/graphql/mutations'
import { getCreditApplicationDataQuery } from 'ppbl/graphql/queries'
import serverData from 'ppbl/utils/server-data'
import { useApplicationContext } from 'ppbl/hooks'
import clientLogger from 'ppbl/utils/client-logger'
import { trackClick, trackPage } from 'ppbl/utils/fpti-helper'
import { FPTI_EVENTS } from 'ppbl/utils/fptiEventNames'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import { interpolate, santizeInputFields } from 'ppbl/utils/string-helper'
import { scrollOnErrors } from 'ppbl/utils/validation-helper'
import React, { useEffect, useRef, useState } from 'react'
import { Field, Form, FormSpy } from 'react-final-form'
import {
  scrollToTopOnContinue,
  createApplicationPayloadGenerator,
  getApplicationFieldsData,
  updateApplicationPayloadGeneratorPage1,
  handleGraphqlError,
} from 'ppbl/screens/__utils__'
import {
  applicationPage1Fields,
  applicationPage1FieldsRenewal,
  getApplicationFields,
} from '../applicationFieldMapper'
import './styles.css'
import { cloneAndMask } from 'ppbl/utils/data-masker'
import createDecorator from 'ppbl/utils/form/decorator'
import {
  PARTNER_CAMP_ID,
  preApprovalCategory,
  PRIVACY_POLICY_PAGE_PATH,
  TERMS_AND_CONDITIONS_PATH,
  UTM_CAMPAIGN,
} from 'ppbl/utils/constants'
import PreQualifiedBanner from 'ppbl/components/PreQualifiedBanner'
import InfoText from 'ppbl/components/InfoText'
import { PhoneNumberFormat } from '@paypalcorp/worldready'
import PreApprovalBanner from 'ppbl/components/PreApprovalBanner'
import Disclaimer from 'ppbl/components/Disclaimer'

type Props = {
  applicationType: string
  creditApplicationLoading: boolean
  eligibilityLoading: boolean
}

const fptiData = {
  page: 'applicationPage1',
  event_name: FPTI_EVENTS.PAGE.PAGE1,
}

const ApplicationPage1: React.FC<Props> = ({
  applicationType,
  creditApplicationLoading,
  eligibilityLoading,
}) => {
  const { data, updateApplicationContext } = useApplicationContext()
  const [isPqBanner, setIsPqBanner] = useState<boolean>(false)

  const { data: cachedApplicationData } = useQuery(
    getCreditApplicationDataQuery,
  )

  // eslint-disable-next-line complexity, max-statements
  useEffect(() => {
    const fieldsToUpdate: Record<string, string> = {}
    if (applicationType !== 'paypal' && applicationType !== 'app') {
      const phoneNumberFormatter = new PhoneNumberFormat({
        phoneRegion: 'US',
      })
      const isPhoneValid = phoneNumberFormatter.parse(
        cachedApplicationData?.personalPhoneNumber || '',
      )

      if (isPhoneValid?.errors?.length > 0) {
        clientLogger.info(
          'Clearing prepopulating invalid personal phone number for renewal application - Application ID:',
          data?.applicationMetaData?.applicationId,
        )
        fieldsToUpdate.personalPhoneNumber = ''
      }

      updateApplicationCachedData(
        {
          ...fieldsToUpdate,
        },
        cachedApplicationData,
      )
    }

    trackPage(
      merge(fptiData, {
        applicationId: data?.applicationMetaData?.applicationId,
      }),
    )
    clientLogger.info('Application Page 1 is loaded')
    const campID =
      localStorage.getItem(PARTNER_CAMP_ID) ||
      sessionStorage.getItem(UTM_CAMPAIGN) ||
      serverData?.startParams?.utm_campaign
    const pqBannerList =
      serverData.data?.appConfig?.application?.pqBannerCampaingList ?? []
    if (campID && pqBannerList.includes(campID)) {
      clientLogger.info(
        `Application page 1 campaign eligible for pre qualified banner - Campaign: ${campID}, applicationType`,
        applicationType,
      )
      setIsPqBanner(true)
    }
  }, [])

  let { newApplicationPage1Fields: applicationPageFields } =
    getApplicationFields(applicationType)

  const isRenewalApplication =
    applicationType === 'app-renewal' || applicationType === 'paypal-renewal'
  const renewalToken = serverData.startParams?.renewalToken
  const isPaypalApplication = applicationType === 'paypal'

  const showReadOnlyCard = isRenewalApplication || isPaypalApplication

  const handleCreateOrUpdateResponse = (response) => {
    clientLogger.info(
      'Response received after creating/updating basic information in application page 1',
      cloneAndMask(response),
    )
    updateApplicationCachedData({ id: response?.id }, cachedApplicationData)
    // push the response.data.credit.businessCredit.createApplication.creditApplication into context
    sessionStorage.setItem('appId', response?.id)
    sessionStorage.setItem('mostRecentCompletedPage', 'applicationPage1')
    updateApplicationContext({
      applicationMetaData: {
        ...data?.applicationMetaData,
        applicationId: response?.id,
        mostRecentCompletedPage: 'applicationPage1',
        legalBusinessName: response?.business?.legalBusinessName,
      },
    })
    navigate(
      `/merchant-lending/loanbuilder/application/us/${applicationType}/applicationPage2`,
    )
  }

  const handleCreateOrUpdateError = (error, callIdentifier) => {
    handleGraphqlError(
      error,
      'Error encountered while creating/updating basic information in application page 1',
      `ApplicationPage 1 - ${callIdentifier}`,
      data?.applicationMetaData?.applicationId,
      renewalToken,
      data?.applicationMetaData?.legalBusinessName,
      applicationType,
    )
  }

  const [createApplication, { loading: createLoading }] = useMutation(
    createApplicationMutation,
    {
      onCompleted(createResponse) {
        handleCreateOrUpdateResponse(
          createResponse?.credit?.businessCredit?.createApplication
            ?.creditApplication,
        )
      },
      onError(createError) {
        handleCreateOrUpdateError(createError, 'createApplicationMutation')
      },
    },
  )
  const [updateApplication, { loading: updateLoading }] = useMutation(
    updateApplicationMutation,
    {
      onCompleted(updateResponse) {
        handleCreateOrUpdateResponse(
          updateResponse?.credit?.businessCredit?.updateApplication
            ?.creditApplication,
        )
      },
      onError(updateError) {
        handleCreateOrUpdateError(updateError, 'updateApplicationMutation')
      },
    },
  )

  const handleApplicationSubmit = async (values: any) => {
    trackClick(
      merge(fptiData, {
        component: 'applicationPage1_continue_button',
        link: 'applicationPage1_continue_button',
        applicationId: data?.applicationMetaData?.applicationId,
        event_name: FPTI_EVENTS.CLICK.PAGE_CONTINUE.PAGE1,
      }),
    )
    scrollToTopOnContinue()

    clientLogger.info(
      'application page 1 submitted - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
    if (isPaypalApplication) {
      clientLogger.info(
        'paypal users encrypted account number',
        serverData?.user?.encryptedAccountNumber,
      )
    } else if (renewalToken) {
      clientLogger.info('renewal user renewal token', renewalToken)
    }
    //marking terms and condition true, inplace of checkbox on continuing
    values.agreedToTermsAndConditions = true
    const applicationValues = showReadOnlyCard
      ? getApplicationFieldsData(
          isRenewalApplication
            ? applicationPage1FieldsRenewal
            : applicationPage1Fields,
          cachedApplicationData,
          values,
        )
      : values

    //sanitize the input values to prevent XSS
    santizeInputFields(applicationValues)

    clientLogger.info(
      'Legal bussiness name for the application',
      applicationValues?.legalBusinessName,
    )

    updateApplicationCachedData(values, cachedApplicationData)
    if (data && data?.applicationMetaData?.applicationId) {
      updateApplication({
        variables: {
          updateInput: updateApplicationPayloadGeneratorPage1(
            applicationValues,
            data.applicationMetaData.applicationId,
          ),
        },
      })
    } else {
      createApplication({
        variables: {
          application: createApplicationPayloadGenerator(applicationValues),
        },
      })
    }
  }

  const getCardFields = () => {
    let cardFields = {}

    if (
      applicationType === 'app-renewal' ||
      applicationType === 'paypal-renewal'
    ) {
      cardFields = {
        'Legal business name': cachedApplicationData.legalBusinessName,
        'First name': cachedApplicationData.firstName,
        'Last name': cachedApplicationData.lastName,
        'Email address': cachedApplicationData.emailAddress,
      }
    } else if (applicationType === 'paypal') {
      cardFields = {
        'PayPal account': cachedApplicationData.emailAddress,
      }
    }
    return cardFields
  }

  const checkboxLabel = (
    <div className="blurbWrapper">
      {interpolate(
        getLocalizedText(
          'application.applicationPages.applicationPage1.consentText',
        ),
        {
          termsAndConditions: 
            <Link
            onClick={()=>navigate(`/merchant-lending/loanbuilder${TERMS_AND_CONDITIONS_PATH}`)}
            size="sm"
            secondary
            >
              {getLocalizedText(
                'application.applicationPages.applicationPage1.termsLabel',
              )}
            </Link>
          ,
          privacyPolicy: (
            <Link
            onClick={()=>navigate(`/merchant-lending/loanbuilder${PRIVACY_POLICY_PAGE_PATH}`)}
            size="sm"
            secondary
            >
              {getLocalizedText(
                'application.applicationPages.applicationPage1.privacyLabel',
              )}
            </Link>
          ),
        },
      )}
    </div>
  )

  const focusOnErrors = createDecorator()

  const isPreApproval: boolean = data?.eligibilityData?.preApproved
  const approvalCategory: preApprovalCategory =
    data?.eligibilityData?.category || ''
  const expiryDate = data?.eligibilityData?.expiryDate

  return (
    <>
      {updateLoading ||
      createLoading ||
      creditApplicationLoading ||
      eligibilityLoading ? (
        <div className="spinner">
          <SpinnerAdapter />
        </div>
      ) : (
        <>
          {isPreApproval && (
            <PreApprovalBanner
              approvalCategory={approvalCategory}
              expiryDate={expiryDate}
            />
          )}
          <Disclaimer approvalCategory={approvalCategory} />
          {isPqBanner && (
            <PreQualifiedBanner
              applicationType={applicationType}
              applicationId={data?.applicationMetaData?.applicationId}
            />
          )}
          <Form onSubmit={handleApplicationSubmit} decorators={[focusOnErrors]}>
            {({ handleSubmit, hasValidationErrors }) => (
              <div className="applicationFormContainer">
                <form onSubmit={handleSubmit}>
                  <div className="applicationTextSection">
                    <Row>
                      <Col form="full">
                        <HeadingText size="sm">
                          {getLocalizedText(
                            'application.applicationPages.applicationPage1.headingText',
                          )}
                        </HeadingText>
                      </Col>
                    </Row>
                    <Row>
                      <Col form="full">
                        <BodyText>
                          {getLocalizedText(
                            'application.applicationPages.applicationPage1.checkEligibilityText',
                          )}
                        </BodyText>
                      </Col>
                    </Row>
                  </div>
                  {showReadOnlyCard && (
                    <ReadOnlyCard
                      id="applicationPage1Card"
                      fieldsList={getCardFields()}
                      isEditable={false}
                    />
                  )}
                  <div className="applicationFields">
                    {applicationPageFields.map((applicationField, index) => {
                      if (Array.isArray(applicationField)) {
                        return (
                          <Row key={`applicationFieldsRow${index}`}>
                            {applicationField.map((field) => {
                              const { name, ...props } = field
                              return (
                                <Col key={name} form="half">
                                  <Field
                                    name={name}
                                    {...props}
                                    initialValue={
                                      cachedApplicationData?.[name] || null
                                    }
                                  />
                                </Col>
                              )
                            })}
                          </Row>
                        )
                      }
                      const { name, ...props } = applicationField
                      const isCheckbox = name === 'agreedToTermsAndConditions'
                      return (
                        <Row key={name}>
                          {isCheckbox ? (
                            <Col className="checkboxGroup" form="full">
                              {checkboxLabel}
                            </Col>
                          ) :  (
                          <Col form="full">
                            {{
                              doYouHaveAFundingCode: (
                                <FormSpy subscription={{ values: true }}>
                                  {({
                                    values: { howDidYouHearAboutThisProduct },
                                  }) =>
                                    howDidYouHearAboutThisProduct ===
                                    'MAILER' && (
                                      <Field
                                      name={name}
                                      {...props}
                                      initialValue={
                                        cachedApplicationData?.[name] ||
                                        null
                                      }
                                      />
                                    )
                                  }
                                </FormSpy>
                              ),
                            }[name] || (
                              <Field
                              name={name}
                              {...props}
                              initialValue={
                                cachedApplicationData?.[name] || null
                              }
                              />
                            )}
                          </Col>
                        )}</Row>
                      )
                    })}
                  </div>
                  <Button
                    className="ctaSection"
                    size="lg"
                    onClick={() => scrollOnErrors(hasValidationErrors)}
                    type="submit"
                  >
                    {getLocalizedText(
                      'application.applicationPages.applicationPage1.continueButtonText',
                    )}
                  </Button>
                </form>
                <InfoText />
              </div>
            )}
          </Form>
        </>
      )}
    </>
  )
}

export default ApplicationPage1
